/**
 * Shared validation and formatting helpers for the phlebotomy intake, address,
 * and mobile-request forms. Kept in one place so the forms cannot drift apart
 * on ZIP, state-abbreviation, phone, or tracking-ID formats.
 */

import { addBusinessDays } from "date-fns";

import {
  PHLEBOTOMY_CAMPAIGN_SOURCE_PARAM,
  PHLEBOTOMY_CUSTOMER_IO_SMS_SOURCE,
} from "../../../utils/urlConstants";

export const US_ZIP_CODE_REGEX = /^\d{5}(?:-\d{4})?$/;
export const US_STATE_ABBREVIATION_FORMAT_REGEX = /^[A-Za-z]{2}$/;

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isUuid = (value: string | null | undefined): value is string =>
  Boolean(value && UUID_REGEX.test(value));

/** Stable per-visit analytics ID for anonymous finder traffic. */
export const createAnonymousId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : 8 + (random % 4);
    return value.toString(16);
  });
};

export interface PhlebotomyCampaignEntry {
  trackingId?: string;
  userId?: string;
}

let initialPhlebotomyCampaignEntry: PhlebotomyCampaignEntry | null | undefined;
const reminderLandingSessionKeys = new Set<string>();

const getReminderLandingSessionKey = (
  entry: PhlebotomyCampaignEntry,
): string | null => {
  if (entry.trackingId) {
    return `phlebotomy-reminder-landing:tracking:${entry.trackingId}`;
  }
  if (entry.userId) {
    return `phlebotomy-reminder-landing:user:${entry.userId}`;
  }
  return null;
};

const claimSessionKey = (
  key: string | null,
  claimedKeys: Set<string>,
): boolean => {
  if (!key || claimedKeys.has(key)) return false;

  if (typeof window === "undefined") {
    claimedKeys.add(key);
    return true;
  }

  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Some privacy modes disable sessionStorage. The module-level set still
    // prevents duplicate events during normal in-app navigation.
  }
  claimedKeys.add(key);
  return true;
};

export const claimPhlebotomyCampaignEntry = (
  entry: PhlebotomyCampaignEntry,
): boolean =>
  claimSessionKey(
    getReminderLandingSessionKey(entry),
    reminderLandingSessionKeys,
  );

/**
 * Returns campaign identity only for an explicit Customer.io SMS landing on
 * the patient's first phlebotomy page load. Intake/CTA navigation and reloads
 * must not count as additional reminder-link clicks.
 */
export const captureInitialPhlebotomyCampaignEntry = (
  search: string,
): PhlebotomyCampaignEntry | null => {
  if (initialPhlebotomyCampaignEntry === undefined) {
    const query = new URLSearchParams(search);
    const rawTrackingId = query.get("tracking_id");
    const trackingId = isUuid(rawTrackingId) ? rawTrackingId : undefined;
    const rawUserId = query.get("user_id")?.trim();
    const userId = rawUserId && rawUserId.length <= 255 ? rawUserId : undefined;
    const isCustomerIoSms =
      query.get(PHLEBOTOMY_CAMPAIGN_SOURCE_PARAM) ===
      PHLEBOTOMY_CUSTOMER_IO_SMS_SOURCE;
    initialPhlebotomyCampaignEntry =
      isCustomerIoSms && (trackingId || userId) ? { trackingId, userId } : null;
  }

  return initialPhlebotomyCampaignEntry;
};

/** @internal test helper */
export const resetInitialPhlebotomyCampaignEntryForTests = (): void => {
  initialPhlebotomyCampaignEntry = undefined;
  if (typeof window !== "undefined") {
    reminderLandingSessionKeys.forEach((key) => {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Ignore disabled storage in tests for the same reason as production.
      }
    });
  }
  reminderLandingSessionKeys.clear();
};

export const isValidUsPhoneNumber = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, "");
  return (
    digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
  );
};

export const formatPhoneForApi = (phone: string): string => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return phone;
};

/**
 * Local-time YYYY-MM-DD, offset by `daysFromToday`. Used for date inputs, which
 * compare as plain strings.
 */
export const getLocalDateString = (daysFromToday = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return formatLocalDateString(date);
};

const formatLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Local-time YYYY-MM-DD after adding weekdays and skipping weekends. Weekends
 * are the only non-business days recognized here; the server applies the same
 * rule, so holidays shorten Customer Support's effective lead time.
 */
export const getLocalBusinessDateString = (businessDays: number): string =>
  formatLocalDateString(addBusinessDays(new Date(), businessDays));
